// Chemin: C:\gestion-copro\src\services\affaire.service.ts
// Session 16 : correction bug undefined pour Firestore
// Session 44 : Nettoyage chronologie lors du détachement de document
// Session 129 : Ajout rattacherCourrier/detacherCourrier avec écriture chronologie

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Affaire,
  CreateAffaireData,
  UpdateAffaireData,
  EvenementAffaire,
  StatutAffaire
} from '../types/affaire.types';

const COLLECTION_NAME = 'affaires';

/**
 * Génère le prochain numéro d'affaire
 * Format: AFF-2026-001
 */
async function generateNumeroAffaire(coproprieteId: string): Promise<string> {
  const year = new Date().getFullYear();
  const prefix = `AFF-${year}-`;
  
  // Récupérer toutes les affaires de l'année pour cette copropriété
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('numero', '>=', prefix),
    where('numero', '<', `AFF-${year + 1}-`)
  );
  
  const snapshot = await getDocs(q);
  const count = snapshot.size;
  const nextNumber = (count + 1).toString().padStart(3, '0');
  
  return `${prefix}${nextNumber}`;
}

/**
 * Supprime les propriétés undefined d'un objet
 * Firestore n'accepte pas les valeurs undefined
 */
function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Crée une nouvelle affaire
 */
export async function createAffaire(
  data: CreateAffaireData,
  userId: string,
  userNom: string
): Promise<string> {
  const numero = await generateNumeroAffaire(data.coproprieteId);
  const now = Timestamp.now();
  
  const evenementCreation: EvenementAffaire = {
    id: crypto.randomUUID(),
    date: now,
    type: 'creation',
    titre: 'Ouverture du dossier',
    description: `Dossier "${data.titre}" créé`,
    auteurId: userId,
    auteurNom: userNom
  };
  
  // Construire l'objet de base (champs obligatoires)
  const affaireBase = {
    coproprieteId: data.coproprieteId,
    numero,
    titre: data.titre,
    type: data.type,
    statut: 'ouverte' as const,
    priorite: data.priorite || 'normale',
    description: data.description,
    dateOuverture: now,
    interlocuteurIds: data.interlocuteurIds || [],
    signalementIds: [],
    documentIds: [],
    evenements: [evenementCreation],
    createdAt: now,
    updatedAt: now,
    createdBy: userId
  };
  
  // Ajouter les champs optionnels SEULEMENT s'ils ont une valeur
  // (Firestore n'accepte pas undefined)
  const affaire: Record<string, unknown> = { ...affaireBase };
  
  if (data.garantieApplicable !== undefined && data.garantieApplicable !== null) {
    affaire.garantieApplicable = data.garantieApplicable;
  }
  
  if (data.montantEstime !== undefined && data.montantEstime !== null) {
    affaire.montantEstime = data.montantEstime;
  }
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), affaire);
  return docRef.id;
}

/**
 * Récupère une affaire par son ID
 */
export async function getAffaireById(id: string): Promise<Affaire | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...snapshot.data()
  } as Affaire;
}

/**
 * Récupère toutes les affaires d'une copropriété
 */
export async function getAffairesByCopropriete(
  coproprieteId: string,
  statutFilter?: StatutAffaire
): Promise<Affaire[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    orderBy('createdAt', 'desc')
  );
  
  // Note: Le filtre par statut nécessite un index composite
  // Pour l'instant, on filtre côté client si nécessaire
  
  const snapshot = await getDocs(q);
  let affaires = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Affaire[];
  
  // Filtrage côté client si statut spécifié
  if (statutFilter) {
    affaires = affaires.filter(a => a.statut === statutFilter);
  }
  
  return affaires;
}

/**
 * Met à jour une affaire
 */
export async function updateAffaire(
  id: string,
  data: UpdateAffaireData
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  
  // Supprimer les champs undefined avant envoi à Firestore
  const cleanData = removeUndefinedFields(data as Record<string, unknown>);
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, {
    ...cleanData,
    updatedAt: Timestamp.now()
  } as any);
}

/**
 * Change le statut d'une affaire
 */
export async function updateStatutAffaire(
  id: string,
  nouveauStatut: StatutAffaire,
  userId: string,
  userNom: string,
  commentaire?: string
): Promise<void> {
  const affaire = await getAffaireById(id);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  const now = Timestamp.now();
  
  const evenement: EvenementAffaire = {
    id: crypto.randomUUID(),
    date: now,
    type: nouveauStatut === 'cloturee' ? 'cloture' : 'decision',
    titre: `Statut changé en "${nouveauStatut}"`,
    description: commentaire,
    auteurId: userId,
    auteurNom: userNom
  };
  
  const updateData: Partial<Affaire> = {
    statut: nouveauStatut,
    updatedAt: now,
    evenements: [...affaire.evenements, evenement]
  };
  
  if (nouveauStatut === 'cloturee') {
    updateData.dateCloture = now;
    updateData.closedBy = userId;
  }
  
  const docRef = doc(db, COLLECTION_NAME, id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, updateData as any);
}

/**
 * Ajoute un événement manuel à la timeline
 */
export async function addEvenementToAffaire(
  affaireId: string,
  evenement: Omit<EvenementAffaire, 'id' | 'date'>
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  const newEvenement: EvenementAffaire = {
    ...evenement,
    id: crypto.randomUUID(),
    date: Timestamp.now()
  };
  
  const docRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(docRef, {
    evenements: [...affaire.evenements, newEvenement],
    updatedAt: Timestamp.now()
  });
}

/**
 * Rattache un signalement à une affaire
 */
export async function rattacherSignalement(
  affaireId: string,
  signalementId: string,
  userId: string,
  userNom: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  if (affaire.signalementIds.includes(signalementId)) {
    return; // Déjà rattaché
  }
  
  const evenement: EvenementAffaire = {
    id: crypto.randomUUID(),
    date: Timestamp.now(),
    type: 'signalement',
    titre: 'Signalement rattaché',
    auteurId: userId,
    auteurNom: userNom,
    signalementId
  };
  
  const docRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(docRef, {
    signalementIds: [...affaire.signalementIds, signalementId],
    evenements: [...affaire.evenements, evenement],
    updatedAt: Timestamp.now()
  });
}

/**
 * Détache un signalement d'une affaire
 * Session 44 : Ajout nettoyage chronologie
 */
export async function detacherSignalement(
  affaireId: string,
  signalementId: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  // Retirer le signalementId ET l'événement de chronologie associé
  const newSignalementIds = affaire.signalementIds.filter(id => id !== signalementId);
  const newEvenements = affaire.evenements.filter(evt => evt.signalementId !== signalementId);
  
  const docRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(docRef, {
    signalementIds: newSignalementIds,
    evenements: newEvenements,
    updatedAt: Timestamp.now()
  });
}

/**
 * Rattache un document à une affaire
 */
export async function rattacherDocument(
  affaireId: string,
  documentId: string,
  documentTitre: string,
  userId: string,
  userNom: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  if (affaire.documentIds.includes(documentId)) {
    return; // Déjà rattaché
  }
  
  const evenement: EvenementAffaire = {
    id: crypto.randomUUID(),
    date: Timestamp.now(),
    type: 'document',
    titre: `Document ajouté: ${documentTitre}`,
    auteurId: userId,
    auteurNom: userNom,
    documentId
  };

  // Session 250 : Poser affaireId sur le document (symétrie avec le chemin SENTINELLE)
  const documentRef = doc(db, 'documents', documentId);
  await updateDoc(documentRef, {
    affaireId,
    affaireNom: affaire.titre || '',
    updatedAt: Timestamp.now()
  });
  
  const docRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(docRef, {
    documentIds: [...affaire.documentIds, documentId],
    evenements: [...affaire.evenements, evenement],
    updatedAt: Timestamp.now()
  });
}

/**
 * Détache un document d'une affaire
 * Session 44 : Ajout nettoyage chronologie
 */
export async function detacherDocument(
  affaireId: string,
  documentId: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  // Session 250 : Vider affaireId sur le document (symétrie)
  const documentRef = doc(db, 'documents', documentId);
  await updateDoc(documentRef, {
    affaireId: null,
    affaireNom: '',
    updatedAt: Timestamp.now()
  });

  // Retirer le documentId ET l'événement de chronologie associé
  const newDocumentIds = affaire.documentIds.filter(id => id !== documentId);
  const newEvenements = affaire.evenements.filter(evt => evt.documentId !== documentId);
  
  const docRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(docRef, {
    documentIds: newDocumentIds,
    evenements: newEvenements,
    updatedAt: Timestamp.now()
  });
}

/**
 * Session 129 : Rattache un courrier à une affaire avec traçabilité chronologie
 * Écrit affaireId sur le courrier + ajoute un événement dans la timeline du dossier
 */
export async function rattacherCourrier(
  affaireId: string,
  courrierId: string,
  courrierObjet: string,
  userId: string,
  userNom: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');

  // Vérifier si un événement courrier existe déjà pour ce courrierId (éviter doublons)
  const dejaTrace = affaire.evenements.some(evt => evt.courrierId === courrierId);
  if (dejaTrace) return;

  const evenement: EvenementAffaire = {
    id: crypto.randomUUID(),
    date: Timestamp.now(),
    type: 'courrier',
    titre: `Courrier rattaché: ${courrierObjet}`,
    auteurId: userId,
    auteurNom: userNom,
    courrierId
  };

  // 1. Écrire affaireId sur le courrier
  const courrierRef = doc(db, 'courriers', courrierId);
  await updateDoc(courrierRef, {
    affaireId,
    updatedAt: Timestamp.now()
  });

  // 2. Ajouter l'événement dans la chronologie du dossier
  const affaireRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(affaireRef, {
    evenements: [...affaire.evenements, evenement],
    updatedAt: Timestamp.now()
  });
}

/**
 * Session 129 : Détache un courrier d'une affaire avec nettoyage chronologie
 * Supprime affaireId du courrier + retire l'événement de la timeline
 */
export async function detacherCourrier(
  affaireId: string,
  courrierId: string
): Promise<void> {
  const affaire = await getAffaireById(affaireId);
  if (!affaire) throw new Error('Affaire non trouvée');

  // 1. Retirer affaireId du courrier
  const courrierRef = doc(db, 'courriers', courrierId);
  await updateDoc(courrierRef, {
    affaireId: '',
    updatedAt: Timestamp.now()
  });

  // 2. Retirer l'événement de chronologie associé
  const newEvenements = affaire.evenements.filter(evt => evt.courrierId !== courrierId);
  const affaireRef = doc(db, COLLECTION_NAME, affaireId);
  await updateDoc(affaireRef, {
    evenements: newEvenements,
    updatedAt: Timestamp.now()
  });
}

/**
 * Supprime une affaire (soft delete recommandé en prod, hard delete ici pour MVP)
 */
export async function deleteAffaire(id: string): Promise<void> {
  const affaire = await getAffaireById(id);
  if (!affaire) throw new Error('Affaire non trouvée');
  
  // Supprimer l'affaire
  const affaireRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(affaireRef);
}

/**
 * Compte les affaires par statut pour une copropriété
 */
export async function countAffairesByStatut(
  coproprieteId: string
): Promise<Record<StatutAffaire, number>> {
  const affaires = await getAffairesByCopropriete(coproprieteId);
  
  const counts: Record<StatutAffaire, number> = {
    ouverte: 0,
    en_cours: 0,
    en_attente: 0,
    cloturee: 0
  };
  
  for (const affaire of affaires) {
    counts[affaire.statut]++;
  }
  
  return counts;
}

/**
 * Récupère les affaires urgentes ou en retard
 */
export async function getAffairesUrgentes(coproprieteId: string): Promise<Affaire[]> {
  const affaires = await getAffairesByCopropriete(coproprieteId);
  
  const now = new Date();
  
  return affaires.filter(affaire => {
    // Priorité urgente
    if (affaire.priorite === 'urgente' && affaire.statut !== 'cloturee') {
      return true;
    }
    
    // Garantie qui expire bientôt (dans les 30 jours)
    if (affaire.dateFinGarantie) {
      const dateFinGarantie = affaire.dateFinGarantie.toDate();
      const joursRestants = Math.ceil(
        (dateFinGarantie.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (joursRestants <= 30 && joursRestants > 0 && affaire.statut !== 'cloturee') {
        return true;
      }
    }
    
    return false;
  });
}
